<template>
  <v-container fluid class="pa-4 rtl">
    <!-- Header -->
    <v-card class="header-card mb-4" elevation="2">
      <div class="d-flex align-center justify-space-between flex-wrap ga-3">
        <div>
          <div class="text-h5 font-weight-bold" style="color: #4a4f55">
            الحواجز المالية أمام التعافي
          </div>
          <div class="text-body-2 text-grey-darken-1 mt-1">
            العوائق المالية التي تحول دون إعادة التأهيل الكامل
          </div>
        </div>
        <v-btn
          prepend-icon="mdi-arrow-right"
          variant="text"
          style="color: #4a4f55"
          @click="showView('pillar-finance')"
        >
          رجوع
        </v-btn>
      </div>
    </v-card>

    <!-- KPIs -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card danger">
          <div class="kpi-value">$3.1M</div>
          <div class="kpi-label">الفجوة التمويلية لإعادة التأهيل</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card danger">
          <div class="kpi-value">0</div>
          <div class="kpi-label">صندوق احتياطي طوارئ</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card warning">
          <div class="kpi-value">2/9</div>
          <div class="kpi-label">لديها خطة مالية للتعافي</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card warning">
          <div class="kpi-value">86%</div>
          <div class="kpi-label">تعتمد على التمويل الخارجي</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Table -->
    <v-card class="table-card" elevation="1">
      <div class="table-header">الحواجز المالية وآليات التغلب عليها</div>
      <v-table class="custom-table">
        <thead>
          <tr>
            <th>المرفق</th>
            <th>الحاجز الرئيسي</th>
            <th>التكلفة المطلوبة</th>
            <th>التمويل المتاح</th>
            <th>الفجوة</th>
            <th>الجهات المحتملة</th>
            <th>خطة التعافي المالي</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in facilities" :key="item.name">
            <td class="font-weight-bold">{{ item.name }}</td>
            <td>{{ item.barrier }}</td>
            <td>{{ item.requiredCost }}</td>
            <td>{{ item.availableFunding }}</td>
            <td class="font-weight-bold" :class="gapClass(item.gapValue)">
              {{ item.gap }}
            </td>
            <td>{{ item.potentialDonors }}</td>
            <td>
              <v-chip
                :color="planColor(item.recoveryPlan)"
                size="small"
                variant="flat"
                >{{ item.recoveryPlan }}</v-chip
              >
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>
  </v-container>
</template>

<script setup>
const facilities = [
  {
    name: "مستشفى الصداقة التعليمي",
    barrier: "ضرر هيكلي بالغ",
    requiredCost: "$1,240,000",
    availableFunding: "$0",
    gap: "$1,240,000",
    gapValue: 1240000,
    potentialDonors: "OCHA/World Bank",
    recoveryPlan: "غائبة",
  },
  {
    name: "مستشفى الأمراض النفسية",
    barrier: "ضرر هيكلي + كادر",
    requiredCost: "$890,000",
    availableFunding: "$0",
    gap: "$890,000",
    gapValue: 890000,
    potentialDonors: "WHO/OCHA",
    recoveryPlan: "غائبة",
  },
  {
    name: "مجمع الشيخ عثمان",
    barrier: "نقص التمويل الجاري",
    requiredCost: "$420,000",
    availableFunding: "$100,000",
    gap: "$320,000",
    gapValue: 320000,
    potentialDonors: "UNOPS/KfW",
    recoveryPlan: "جزئية",
  },
  {
    name: "مركز كود العثماني",
    barrier: "نقص الكادر المالي",
    requiredCost: "$180,000",
    availableFunding: "$80,000",
    gap: "$100,000",
    gapValue: 100000,
    potentialDonors: "وزارة الصحة",
    recoveryPlan: "جزئية",
  },
  {
    name: "مركز الممدارة",
    barrier: "صيانة دورية",
    requiredCost: "$95,000",
    availableFunding: "$50,000",
    gap: "$45,000",
    gapValue: 45000,
    potentialDonors: "وزارة الصحة",
    recoveryPlan: "موجودة",
  },
  {
    name: "مركز المحاريق",
    barrier: "انقطاع التمويل",
    requiredCost: "$160,000",
    availableFunding: "$30,000",
    gap: "$130,000",
    gapValue: 130000,
    potentialDonors: "وزارة الصحة",
    recoveryPlan: "غائبة",
  },
  {
    name: "مركز عبد القوي",
    barrier: "صيانة",
    requiredCost: "$75,000",
    availableFunding: "$40,000",
    gap: "$35,000",
    gapValue: 35000,
    potentialDonors: "وزارة الصحة",
    recoveryPlan: "موجودة",
  },
  {
    name: "مركز ماري ستبوس",
    barrier: "إغلاق المرفق كلياً",
    requiredCost: "$210,000",
    availableFunding: "$0",
    gap: "$210,000",
    gapValue: 210000,
    potentialDonors: "UNFPA",
    recoveryPlan: "غائبة",
  },
  {
    name: "جمعية حقوق الطفل",
    barrier: "صيانة بسيطة",
    requiredCost: "$45,000",
    availableFunding: "$30,000",
    gap: "$15,000",
    gapValue: 15000,
    potentialDonors: "منظمة",
    recoveryPlan: "موجودة",
  },
];

const gapClass = (value) => {
  if (value >= 130000) return "text-red-darken-2";
  if (value >= 35000) return "text-orange-darken-2";
  return "text-green-darken-2";
};

const planColor = (value) => {
  if (value === "موجودة") return "green";
  if (value === "جزئية") return "orange";
  return "red";
};

const showView = (view) => console.log("Go to:", view);
</script>

<style scoped>
.rtl {
  direction: rtl;
}

.header-card {
  border-right: 5px solid #4a4f55;
  border-radius: 18px;
  padding: 20px;
}

.kpi-card {
  border-radius: 18px;
  padding: 20px;
  text-align: center;
}

.kpi-value {
  font-size: 34px;
  font-weight: 800;
}

.kpi-label {
  margin-top: 6px;
  font-size: 13px;
}

.danger {
  background: #fdecea;
  color: #c0392b;
}

.warning {
  background: #fff4e5;
  color: #d4821a;
}

.table-card {
  border-radius: 18px;
  overflow: hidden;
}

.table-header {
  padding: 18px;
  font-size: 18px;
  font-weight: 700;
  color: #3a3f47;
  border-bottom: 1px solid #eee;
}

.custom-table {
  direction: rtl;
}

:deep(th) {
  font-weight: 800 !important;
  color: #3a3f47 !important;
  white-space: nowrap;
}

:deep(td) {
  font-size: 14px;
  white-space: nowrap;
}
</style>
